import * as React from "react";
import { Link } from "react-router-dom";
import BackButton from "../../core/BackButton/BackButton";
import Spinner from "../../core/Spinner/Spinner";
import TableBooleanFilter from "../../core/TableBooleanFilter/TableBooleanFilter";
import TableTextFilter from "../../core/TableTextFilter/TableTextFilter";
import debounce from "../../helpers/debounce";
import { sessionGetItem, sessionSetItem } from "../../helpers/storage";
import { ITranslationProps, translateComponent } from "../../helpers/translation";
import IListResults from "../../types/IListResults";
import IResponse from "../../types/IResponse";
import IUserSearchRequest from "../../types/IUserSearchRequest";
import IUserSearchResult from "../../types/IUserSearchResult";
import UserRow from "./UserRow";

interface IUserListPersistedState {
    name: string;
    email: string;
    isActive: boolean;
}

interface IUserListProps extends ITranslationProps {
    loading: boolean;
    searchUsers: (criteria: IUserSearchRequest) => Promise<IResponse<IListResults<IUserSearchResult>> | null>;
}

interface IUserListState {
    name: string;
    email: string;
    isActive: boolean;
    users: IUserSearchResult[];
    moreRecordsExist: boolean;
    page: number;
}

class UserList extends React.Component<IUserListProps, IUserListState> {

    private stateStorageKey = "userList_state_storage";

    private searchWithDebounce = debounce(() => { this.search(); }, 500);

    constructor(props: IUserListProps) {
        super(props);

        this.state = { name: "", email: "", isActive: true, users: [], moreRecordsExist: false, page: 1 };

        this.updateEmail = this.updateEmail.bind(this);
        this.updateName = this.updateName.bind(this);
        this.search = this.search.bind(this);
        this.onActiveStatusChange = this.onActiveStatusChange.bind(this);
        this.loadNextPage = this.loadNextPage.bind(this);
    }

    public componentDidMount() {
        const savedState = sessionGetItem<IUserListPersistedState>(this.stateStorageKey);
        if (savedState) {
            this.setState({
                email: savedState.email,
                isActive: savedState.isActive,
                name: savedState.name
            }, () => {
                this.search();
            });
        } else {
            this.search();
        }
    }

    public componentWillUnmount() {

        const persistedState: IUserListPersistedState = {
            email: this.state.email,
            isActive: this.state.isActive,
            name: this.state.name
        }

        sessionSetItem(this.stateStorageKey, persistedState);
    }

    public render() {

        const userRows: React.ReactNode[] = [];

        this.state.users.forEach(user => {
            userRows.push(<UserRow key={user.id} {...user} />);
        });

        return (
            <div>
                <BackButton />
                <div>
                    <h2 className="float-left">{this.props.t("UserList.Users")}</h2>
                    <Link className="btn btn-primary float-right" to="/user">{this.props.t("UserList.CreateNewUser")}</Link>
                </div>

                {this.props.loading && <Spinner />}

                <table className="table table-sm">
                    <thead className="thead-light">
                        <tr>
                            <th>
                                <div>{this.props.t("UserList.Name")}</div>
                                <TableTextFilter value={this.state.name} onChange={this.updateName} searchButtonClick={this.search} />
                            </th>
                            <th>
                                <div>{this.props.t("UserList.Email")}</div>
                                <TableTextFilter value={this.state.email} onChange={this.updateEmail} searchButtonClick={this.search} />
                            </th>
                            <th>
                                <div>{this.props.t("UserList.UserStatus")}</div>
                                <TableBooleanFilter value={this.state.isActive} trueLabel={this.props.t("Global.Active")} falseLabel={this.props.t("Global.Inactive")} onChange={this.onActiveStatusChange} />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {userRows}
                    </tbody>
                </table>
                {
                    this.state.moreRecordsExist &&
                    <button data-id="showMoreButton" className="btn btn-outline-secondary" onClick={this.loadNextPage}>{this.props.t("UserList.ShowMore")}
                    </button>
                }
            </div>
        );
    }

    private onActiveStatusChange(value: boolean) {
        // Note that we're calling search with no debounce here since it's a boolean toggle
        this.setState({ isActive: value }, this.search);
    }

    private updateName(value: string) {
        this.setState({ name: value }, this.searchWithDebounce);
    }

    private updateEmail(value: string) {
        this.setState({ email: value }, this.searchWithDebounce);
    }

    private async search() {
        const response = await this.props.searchUsers(this.getSearchRequest());

        if (response && response.success) {
            if (this.state.page === 1) {
                this.setState({ users: response.payload.records, moreRecordsExist: response.payload.moreRecordsExist });
            } else {
                this.setState((state: IUserListState) => {
                    state.users.push(...response.payload.records);
                    state.moreRecordsExist = response.payload.moreRecordsExist;
                    return state;
                });
            }
        }
    }

    private loadNextPage() {
        this.setState({ page: this.state.page + 1 }, this.search);
    }

    private getSearchRequest(): IUserSearchRequest {
        return {
            email: this.state.email,
            name: this.state.name,
            includeInactiveUsers: !this.state.isActive,
            page: this.state.page
        }
    }
}

export default translateComponent(UserList);



// WEBPACK FOOTER //
// ./src/components/UserList/UserList.tsx